import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';

import type { PayloadBuilderState } from '~/utils/societePayloadBuilder/types';

export interface PayloadFormSectionProps {
  state: PayloadBuilderState;
  onChange: (next: PayloadBuilderState) => void;
}

export function updatePayloadState(
  state: PayloadBuilderState,
  onChange: (next: PayloadBuilderState) => void,
  patch: Partial<PayloadBuilderState>,
) {
  onChange({ ...state, ...patch });
}

interface SectionCardProps {
  title: string;
  subtitle?: string;
  count?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

export function SectionCard({ title, subtitle, count, defaultExpanded = false, children }: SectionCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const accent = theme.palette.primary.main;
  const label = count !== undefined ? `${title} (${count})` : title;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((value) => !value);
          }
        }}
        sx={{
          px: 1.75,
          py: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
          bgcolor: alpha(accent, 0.06),
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            {label}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          color="action"
          sx={{
            flexShrink: 0,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
          }}
        />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.75, py: 1.25 }}>
          <Stack spacing={2}>{children}</Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

/** Styles all required markers (MUI labels and custom FieldLabel) consistently in red. */
export const PAYLOAD_BUILDER_REQUIRED_FIELDS_SX = {
  '& .MuiFormLabel-asterisk': {
    color: 'error.main',
  },
} as const;

/** Matches MUI InputLabel required indicator markup so styling stays consistent. */
export function RequiredMark() {
  return <span className="MuiFormLabel-asterisk"> *</span>;
}

interface FieldLabelProps {
  children: React.ReactNode;
  required?: boolean;
  sx?: SxProps<Theme>;
}

export function FieldLabel({ children, required, sx }: FieldLabelProps) {
  return (
    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, ...sx }}>
      {children}
      {required ? <RequiredMark /> : null}
    </Typography>
  );
}

interface FieldLabelWithLinkProps {
  label: string;
  required?: boolean;
  href?: string;
  htmlFor?: string;
}

export function FieldLabelWithLink({ label, required, href, htmlFor }: FieldLabelWithLinkProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography component="label" htmlFor={htmlFor} variant="body2" fontWeight={500}>
        {label}
        {required ? <RequiredMark /> : null}
      </Typography>
      {href ? (
        <IconButton
          component={Link}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          aria-label={`Open ${label}`}
          sx={{ color: 'text.secondary', p: 0.25, '&:hover': { color: 'primary.main' } }}
        >
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </IconButton>
      ) : null}
    </Stack>
  );
}

export const PAYLOAD_BUILDER_MONO_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export const PAYLOAD_BUILDER_ALERT_BUTTON_SX = {
  flexShrink: 0,
  width: { xs: '100%', sm: 'auto' },
} as const;
